import { Button } from "@/components/ui/button";
import { Phone, Mic2, Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export const Navigation = () => {
  const [showPhone, setShowPhone] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => setShowPhone(prev => !prev), 3000);
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => { clearInterval(interval); window.removeEventListener("scroll", handleScroll); };
  }, []);

  const scrollToSection = (id: string) => {
    setMenuOpen(false);
    
    if (location.pathname === "/") {
      // On homepage - just scroll
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      // On subpage - navigate to homepage with hash
      navigate(`/#${id}`);
    }
  };

  // Handle hash scroll after navigation
  useEffect(() => {
    if (location.pathname === "/" && location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [location]);

  const navLinks = [
    { id: "about", label: "O nas" },
    { id: "offer", label: "Oferta" },
    { id: "benefits", label: "Dlaczego warto?" },
    { id: "why-us", label: "Dlaczego my?" },
    { id: "kontakt", label: "Kontakt" },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled ? "bg-background/80 backdrop-blur-xl border-b border-border shadow-lg shadow-background/50" : "bg-transparent"
    }`}>
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        <a href="/" className="text-xl font-space-grotesk font-bold text-foreground flex items-center gap-2 hover:text-primary transition-colors">
          <Mic2 className="w-7 h-7 text-primary" />
          Wystep.pl
        </a>

        {/* Desktop nav */}
        <div className="hidden md:flex gap-1 items-center">
          {navLinks.map(link => (
            <button 
              key={link.id}
              onClick={() => scrollToSection(link.id)} 
              className="text-muted-foreground hover:text-foreground px-3 py-2 rounded-lg hover:bg-secondary transition-all duration-200 text-sm"
            >
              {link.label}
            </button>
          ))}
          <Button 
            onClick={() => window.location.href = "tel:+48533333993"}
            className="ml-2 bg-primary hover:bg-primary/80 text-primary-foreground w-[180px] rounded-lg transition-all duration-300 flex items-center justify-center gap-2 glow"
          >
            {showPhone ? (
              <><Phone className="w-4 h-4" /> +48 533 333 993</>
            ) : "Zamów występ"}
          </Button>
        </div>

        {/* Mobile menu button */}
        <button 
          className="md:hidden text-foreground p-2"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-background/95 backdrop-blur-xl border-t border-border animate-fade-in">
          <div className="container mx-auto px-4 py-4 flex flex-col gap-1">
            {navLinks.map(link => (
              <button 
                key={link.id}
                onClick={() => scrollToSection(link.id)} 
                className="text-muted-foreground hover:text-foreground px-4 py-3 rounded-lg hover:bg-secondary transition-all text-left"
              >
                {link.label}
              </button>
            ))}
            <Button 
              onClick={() => window.location.href = "tel:+48533333993"}
              className="mt-2 bg-primary hover:bg-primary/80 text-primary-foreground rounded-lg"
            >
              +48 533 333 993
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};
